import { useState, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import {
  type FilterValues,
  type SortColumn,
  createInitialFilterValues,
  createDefaultDateRange,
  type FilterInitializationContext,
  MODAL_TYPES,
} from './transactionTypes';
import {
  trackFilterSubmit,
  trackFilterReset,
  trackPageChange,
  trackFilterSubmitInsights,
  trackPageChangeInsights,
  type UserContext,
} from './transactionTracking';
interface UseTransactionsFiltersProps {
  readonly accountId: string;
  readonly cardNo: string;
  readonly modalType?: string;
  readonly loggedInUser: any;
  readonly assignedAccountNumber: string;
}

/**
 * Custom hook for managing transactions filter state and handlers
 *
 * RESPONSIBILITIES:
 * - Filter state management
 * - Pagination state management
 * - Sort state management
 * - User interaction handlers
 * - Tracking integration (delegates to transactionTracking)
 */
export const useTransactionsFilters = ({
  accountId,
  cardNo,
  modalType,
  loggedInUser,
  assignedAccountNumber,
}: UseTransactionsFiltersProps) => {
  // Create date range once using factory function
  const dateRange = useMemo(() => createDefaultDateRange(), []);

  // Create initialization context
  const initContext: FilterInitializationContext = useMemo(
    () => ({ accountId, cardNo, modalType }),
    [accountId, cardNo, modalType]
  );

  // Initialize filter state using factory function (prevents invalid states)
  const [filterValues, setFilterValues] = useState<FilterValues>(() =>
    createInitialFilterValues(initContext, dateRange.startDate, dateRange.endDate)
  );

  // Filter panel visibility — modal mode starts hidden, standalone starts visible
  const [filterDisplay, setFilterDisplay] = useState(!modalType);

  const toggleFilter = useCallback(() => {
    setFilterDisplay((prev) => !prev);
  }, []);

  // User context for tracking (memoized to prevent unnecessary re-tracking)
  const userContext: UserContext = useMemo(
    () => ({ loggedInUser, assignedAccountNumber }),
    [loggedInUser, assignedAccountNumber]
  );

  /**
   * Handle page number change
   * Tracks to GTM and App Insights, then updates state
   */
  const handlePageChange = useCallback(
    (pageNumber: number) => {
      trackPageChange(pageNumber);
      trackPageChangeInsights(userContext);

      setFilterValues((prev) => ({
        ...prev,
        pageNumber,
        triggerFilter: true,
      }));
    },
    [userContext]
  );

  /**
   * Handle column sorting
   * No tracking required for sort changes
   */
  const handleColumnSort = useCallback((column: SortColumn, isAsc: boolean) => {
    setFilterValues((prev) => ({
      ...prev,
      sortColumn: column,
      sortDirection: isAsc,
      triggerFilter: true,
    }));
  }, []);

  /**
   * Reset filter values to defaults
   * @param isFilterClick - Whether reset was triggered by user action
   * @param triggerFilter - Whether to trigger data fetch after reset
   * @param clearDates - Whether to clear date fields (false for modal mode)
   */
  const resetFilterValues = useCallback(
    (isFilterClick = false, triggerFilter = false, clearDates = true) => {
      // Centralized tracking
      trackFilterReset(modalType);

      setFilterValues((prev) => ({
        ...prev,
        contractId: modalType ? accountId : '',
        cardNo: modalType === MODAL_TYPES.CARD ? cardNo : '',
        vehicleRegNo: '',
        driverName: '',
        sourceNo: '',
        startDate: clearDates ? null : dateRange.startDate,
        endDate: clearDates ? null : dateRange.endDate,
        pageNumber: 1,
        isFilterClick,
        triggerFilter,
      }));
    },
    [modalType, accountId, cardNo, dateRange]
  );

  /**
   * Handle filter form submission
   * @param data - Form data from filter submission
   * @param toggleFilter - Callback to close filter panel
   */
  const handleSubmitFilter = useCallback(
    (data: Partial<FilterValues>) => {
      const nextValues: FilterValues = {
        ...filterValues,
        ...data,
        pageNumber: 1,
        startDate: data?.startDate ? dayjs(data.startDate).format('DD/MM/YYYY') : null,
        endDate: data?.endDate ? dayjs(data.endDate).format('DD/MM/YYYY') : null,
        contractId: data?.contractId ?? '',
        cardNo: data?.cardNo ?? '',
        triggerFilter: true,
        isFilterClick: true,
      };

      setFilterValues(nextValues);
      trackFilterSubmit(nextValues, modalType);
      trackFilterSubmitInsights(userContext);
    },
    [filterValues, modalType, userContext]
  );

  return {
    filterValues,
    setFilterValues,
    handlePageChange,
    handleColumnSort,
    resetFilterValues,
    handleSubmitFilter,
    filterDisplay,
    setFilterDisplay,
    toggleFilter,
  };
};
